import { formatISOToTime } from "~/src/lib/utils/date-utils";

export type PopupImportScheduleOption = {
  label: string;
  value: string;
  /** Raw UTC minutes-from-midnight for start; null if no start. */
  _utcStartMin: number | null;
  /** Raw UTC minutes-from-midnight for end; null if no end. */
  _utcEndMin: number | null;
};

type ScheduleEdge = {
  readonly node: {
    readonly id: string;
    readonly startTime: string | null;
    readonly endTime: string | null;
  } | null;
} | null;

function isoToUtcMinutes(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

export function buildPopupImportScheduleOptions(
  edges: readonly ScheduleEdge[] | null | undefined
): PopupImportScheduleOption[] {
  return (
    edges?.flatMap((e) => {
      if (!e?.node) return [];
      const { id, startTime, endTime } = e.node;
      const startLabel = formatISOToTime(startTime);
      const endLabel = formatISOToTime(endTime);
      return [
        {
          label: `${startLabel || ""} ~ ${endLabel || ""}`,
          value: id,
          _utcStartMin: isoToUtcMinutes(startTime),
          _utcEndMin: isoToUtcMinutes(endTime),
        },
      ];
    }) ?? []
  );
}

/** Parse a CSV time fragment like "05:30" to total minutes from midnight. */
function parseTimeToMinutes(t: string): number | null {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/**
 * Resolve CSV-exported schedule time labels to schedule option IDs.
 * Handles timezone mismatch: CSV exports in server timezone, options display in browser timezone.
 * Auto-detects the offset by finding a consistent hour difference.
 */
export function resolveScheduleLabelsToIds(
  csvScheduleIds: string[],
  scheduleOptions: PopupImportScheduleOption[]
): string[] {
  if (!csvScheduleIds.length || !scheduleOptions.length) return csvScheduleIds;

  // Parse CSV entries into start/end minutes
  const csvParsed = csvScheduleIds.map((raw) => {
    const t = raw.trim();
    const dashIdx = t.indexOf("-");
    if (dashIdx === -1)
      return { raw: t, startMin: parseTimeToMinutes(t), endMin: null };
    const startStr = t.slice(0, dashIdx).trim();
    const endStr = t.slice(dashIdx + 1).trim();
    return {
      raw: t,
      startMin: startStr ? parseTimeToMinutes(startStr) : null,
      endMin: endStr ? parseTimeToMinutes(endStr) : null,
    };
  });

  // Detect server timezone offset relative to UTC by finding consistent offset
  // between CSV minutes and option UTC minutes.
  const offsetVotes: Map<number, number> = new Map();
  for (const csv of csvParsed) {
    for (const opt of scheduleOptions) {
      if (csv.startMin != null && opt._utcStartMin != null) {
        const diff =
          (((csv.startMin - opt._utcStartMin) % (24 * 60)) + 24 * 60) %
          (24 * 60);
        // Also verify end matches with the same offset (if both present)
        if (csv.endMin != null && opt._utcEndMin != null) {
          const endDiff =
            (((csv.endMin - opt._utcEndMin) % (24 * 60)) + 24 * 60) % (24 * 60);
          if (diff === endDiff) {
            offsetVotes.set(diff, (offsetVotes.get(diff) ?? 0) + 2);
          }
        } else if (csv.endMin == null && opt._utcEndMin == null) {
          offsetVotes.set(diff, (offsetVotes.get(diff) ?? 0) + 1);
        } else if (csv.endMin != null && opt._utcEndMin == null) {
          // mismatch
        } else {
          // csv has no end, option has end — not a match
        }
      } else if (csv.startMin == null && opt._utcStartMin == null) {
        if (csv.endMin != null && opt._utcEndMin != null) {
          const diff =
            (((csv.endMin - opt._utcEndMin) % (24 * 60)) + 24 * 60) % (24 * 60);
          offsetVotes.set(diff, (offsetVotes.get(diff) ?? 0) + 1);
        }
      }
    }
  }

  // Pick the offset with the most votes
  let bestOffset = 0;
  let bestVotes = 0;
  for (const [offset, votes] of Array.from(offsetVotes)) {
    if (votes > bestVotes) {
      bestOffset = offset;
      bestVotes = votes;
    }
  }

  // Now resolve each CSV entry using the detected offset
  return csvParsed
    .map((csv) => {
      // Convert CSV minutes to UTC using the detected offset
      const csvStartUtc =
        csv.startMin != null
          ? (((csv.startMin - bestOffset) % (24 * 60)) + 24 * 60) % (24 * 60)
          : null;
      const csvEndUtc =
        csv.endMin != null
          ? (((csv.endMin - bestOffset) % (24 * 60)) + 24 * 60) % (24 * 60)
          : null;

      for (const opt of scheduleOptions) {
        const startMatch =
          csvStartUtc === opt._utcStartMin ||
          (csvStartUtc == null && opt._utcStartMin == null);
        const endMatch =
          csvEndUtc === opt._utcEndMin ||
          (csvEndUtc == null && opt._utcEndMin == null);
        if (startMatch && endMatch) {
          return opt.value;
        }
      }
      // No match — keep raw label for fallback display
      return csv.raw;
    })
    .filter(Boolean);
}
